import {
  AlarmClock,
  AlertTriangle,
  ArrowLeftRight,
  Banknote,
  Bell,
  BookCopy,
  BookHeart,
  BookMarked,
  BookOpen,
  BookOpenCheck,
  Boxes,
  Briefcase,
  Building2,
  Bus,
  BusFront,
  CalendarCheck,
  CalendarClock,
  CalendarDays,
  CalendarRange,
  ChartColumn,
  ClipboardCheck,
  Clock,
  Coins,
  FileBarChart,
  FileText,
  FileUp,
  FileUser,
  FolderTree,
  GraduationCap,
  HandCoins,
  HeartHandshake,
  History,
  IdCard,
  KeyRound,
  Landmark,
  Layers,
  LayoutGrid,
  LibraryBig,
  ListChecks,
  Megaphone,
  Navigation,
  Network,
  Package,
  PackageSearch,
  PackageX,
  Percent,
  Presentation,
  Receipt,
  Route as RouteIcon,
  School,
  ScrollText,
  Settings2,
  Shield,
  ShieldCheck,
  ShoppingCart,
  SlidersHorizontal,
  Sparkles,
  Tags,
  Truck,
  Undo2,
  UserCheck,
  UserCog,
  UserPlus,
  Users,
  UsersRound,
  Wallet,
  Warehouse,
  Wrench,
} from 'lucide-react';
import type { ComponentType } from 'react';
import { ROUTES } from './routes';
import { PERMISSIONS } from '@/types/permissions';

/*
 * School OS — the product's map, declared once.
 *
 * Three levels: the launcher lists MODULES, a module dashboard lists its
 * sub-modules, and each sub-module is an existing page. Everything that needs
 * to know "what the product contains" reads from here — the launcher, the
 * module dashboards, the sidebar's module list, the breadcrumb and the search
 * palette (through NAVIGATION, derived below) — so a page cannot be offered in
 * one place and missing from another.
 *
 * Visibility is presentation only. A sub-module is shown when the user holds
 * any of its permissions (mirroring the route guard in AppRouter), a module
 * when at least one of its sub-modules is shown. Every route is separately
 * guarded and the backend authorises every request regardless.
 */

export type ModuleAccent =
  | 'blue'
  | 'indigo'
  | 'violet'
  | 'emerald'
  | 'teal'
  | 'amber'
  | 'orange'
  | 'rose'
  | 'cyan'
  | 'slate';

export type Icon = ComponentType<{ className?: string }>;

export interface SubModule {
  key: string;
  title: string;
  description: string;
  path: string;
  /** Shown when the user holds any one of these; must match the route guard. */
  permissions: string[];
  icon: Icon;
}

export interface ModuleDef {
  /** URL slug: the module dashboard lives at /dashboard/{key}. */
  key: string;
  title: string;
  description: string;
  icon: Icon;
  accent: ModuleAccent;
  subModules: SubModule[];
}

export const moduleDashboardPath = (key = ':moduleKey') => `/dashboard/${key}`;

const P = PERMISSIONS;

export const MODULES: ModuleDef[] = [
  {
    key: 'my-school',
    title: 'My School',
    description: 'Attendance, timetable, results, fees and more — for you or your children',
    icon: School,
    accent: 'blue',
    subModules: [
      { key: 'children', title: 'My children', description: 'Each child at a glance', path: ROUTES.myChildren, permissions: [P.STUDENT_CHILD_VIEW], icon: HeartHandshake },
      { key: 'overview', title: 'Overview', description: 'Your day, your attendance and your latest results', path: ROUTES.myOverview, permissions: [P.STUDENT_SELF_VIEW], icon: LayoutGrid },
      { key: 'attendance', title: 'Attendance', description: 'Days present, absent and on leave', path: ROUTES.myAttendance, permissions: [P.STUDENT_SELF_VIEW, P.STUDENT_CHILD_VIEW], icon: CalendarCheck },
      { key: 'timetable', title: 'Timetable', description: 'The week of lessons', path: ROUTES.myTimetable, permissions: [P.STUDENT_SELF_VIEW, P.STUDENT_CHILD_VIEW], icon: Presentation },
      { key: 'results', title: 'Results', description: 'Published results and report cards', path: ROUTES.myResults, permissions: [P.STUDENT_SELF_VIEW, P.STUDENT_CHILD_VIEW], icon: ClipboardCheck },
      { key: 'fees', title: 'Fees', description: 'Balance, invoices, payments and receipts', path: ROUTES.myFees, permissions: [P.STUDENT_SELF_VIEW, P.STUDENT_CHILD_VIEW], icon: Wallet },
      { key: 'assignments', title: 'Assignments', description: 'Set work, due dates and feedback', path: ROUTES.myAssignments, permissions: [P.STUDENT_SELF_VIEW, P.STUDENT_CHILD_VIEW], icon: BookOpenCheck },
      { key: 'bus', title: 'School bus', description: 'Route, stop and today\'s trips', path: ROUTES.myBus, permissions: [P.STUDENT_SELF_VIEW, P.STUDENT_CHILD_VIEW], icon: BusFront },
      { key: 'library', title: 'Library account', description: 'Books on loan and any fines', path: ROUTES.myLibraryAccount, permissions: [P.STUDENT_SELF_VIEW, P.STUDENT_CHILD_VIEW], icon: BookMarked },
    ],
  },
  {
    key: 'students',
    title: 'Students',
    description: 'Student records, admissions, enrolment and attendance',
    icon: Users,
    accent: 'blue',
    subModules: [
      { key: 'profiles', title: 'Student profiles', description: 'Records, guardians, documents and status history', path: ROUTES.students, permissions: [P.STUDENT_VIEW], icon: GraduationCap },
      { key: 'admissions', title: 'Admissions', description: 'Applications from enquiry to enrolment', path: ROUTES.admissions, permissions: [P.ADMISSION_VIEW], icon: FileUser },
      { key: 'enrollments', title: 'Enrolments', description: 'Placement of students in classes by year', path: ROUTES.enrollments, permissions: [P.ENROLLMENT_VIEW], icon: UserPlus },
      { key: 'guardians', title: 'Guardians', description: 'Parents and guardians, contacts and portal accounts', path: ROUTES.guardians, permissions: [P.GUARDIAN_VIEW], icon: HeartHandshake },
      { key: 'promotion', title: 'Promotion', description: 'Move classes up at the end of the year', path: ROUTES.promotion, permissions: [P.ENROLLMENT_PROMOTE], icon: Sparkles },
      { key: 'attendance', title: 'Attendance', description: 'Daily registers, corrections and attendance reports', path: ROUTES.attendance, permissions: [P.STUDENT_ATTENDANCE_VIEW], icon: CalendarCheck },
    ],
  },
  {
    key: 'teaching',
    title: 'Teaching',
    description: 'Timetables, teaching assignments, registers and coursework',
    icon: BookOpen,
    accent: 'indigo',
    subModules: [
      { key: 'my-teaching', title: 'My teaching', description: 'Your week, today\'s lessons and registers to take', path: ROUTES.myTeaching, permissions: [P.TIMETABLE_VIEW], icon: CalendarClock },
      { key: 'timetables', title: 'Timetables', description: 'Weekly timetables per section, drafts and publishing', path: ROUTES.timetables, permissions: [P.TIMETABLE_VIEW], icon: Presentation },
      { key: 'teacher-assignments', title: 'Teaching assignments', description: 'Who teaches which subject to which section', path: ROUTES.teacherAssignments, permissions: [P.TEACHER_VIEW], icon: UserCheck },
      { key: 'coursework', title: 'Coursework', description: 'Assignments, hand-ins and feedback', path: ROUTES.assignments, permissions: [P.ASSIGNMENT_VIEW], icon: BookOpenCheck },
      { key: 'attendance', title: 'Attendance registers', description: 'Take and correct class registers', path: ROUTES.attendance, permissions: [P.STUDENT_ATTENDANCE_VIEW], icon: CalendarCheck },
      { key: 'curriculum', title: 'Curriculum', description: 'Subjects offered per grade and weekly periods', path: ROUTES.curriculum, permissions: [P.ACADEMIC_SUBJECT_VIEW], icon: ScrollText },
      { key: 'periods', title: 'Periods', description: 'Bell times that timetables are built on', path: ROUTES.periods, permissions: [P.PERIOD_VIEW], icon: Clock },
    ],
  },
  {
    key: 'examinations',
    title: 'Examinations',
    description: 'Exams, schedules, marks entry, results and report cards',
    icon: ClipboardCheck,
    accent: 'violet',
    subModules: [
      { key: 'exams', title: 'Examinations', description: 'Plan exams; enter marks, publish results and print report cards from each exam', path: ROUTES.examinations, permissions: [P.EXAMINATION_VIEW], icon: ClipboardCheck },
    ],
  },
  {
    key: 'finance',
    title: 'Finance',
    description: 'Fees, invoices, payments, receipts and financial reports',
    icon: Wallet,
    accent: 'emerald',
    subModules: [
      { key: 'overview', title: 'Finance overview', description: 'Collections, outstanding and overdue at a glance', path: ROUTES.financeDashboard, permissions: [P.FINANCE_REPORT_VIEW], icon: Landmark },
      { key: 'invoices', title: 'Invoices', description: 'Issued fees, balances and adjustments', path: ROUTES.invoices, permissions: [P.INVOICE_VIEW], icon: FileText },
      { key: 'payments', title: 'Payments & receipts', description: 'Record payments, allocate them and print receipts', path: ROUTES.payments, permissions: [P.PAYMENT_VIEW], icon: Banknote },
      { key: 'structures', title: 'Fee structures', description: 'What each grade pays, by category and term', path: ROUTES.feeStructures, permissions: [P.FEE_STRUCTURE_VIEW], icon: Layers },
      { key: 'fee-assignments', title: 'Student fees', description: 'Fee structures assigned to students', path: ROUTES.feeAssignments, permissions: [P.FEE_ASSIGNMENT_VIEW], icon: Receipt },
      { key: 'discounts', title: 'Discounts', description: 'Sibling, staff and other discounts with approval', path: ROUTES.discounts, permissions: [P.DISCOUNT_VIEW], icon: Percent },
      { key: 'scholarships', title: 'Scholarships', description: 'Merit and need-based awards', path: ROUTES.scholarships, permissions: [P.SCHOLARSHIP_VIEW], icon: GraduationCap },
      { key: 'refunds', title: 'Refunds', description: 'Money returned to families, with approval', path: ROUTES.refunds, permissions: [P.REFUND_VIEW], icon: Undo2 },
      { key: 'reports', title: 'Financial reports', description: 'Collections, defaulters, ledgers and exports', path: ROUTES.financeReports, permissions: [P.FINANCE_REPORT_VIEW], icon: FileBarChart },
      { key: 'setup', title: 'Fee setup', description: 'Fee categories, late fees and finance settings', path: ROUTES.financeSetup, permissions: [P.FEE_CATEGORY_VIEW], icon: Settings2 },
    ],
  },
  {
    key: 'staff',
    title: 'Staff',
    description: 'Staff directory, teachers and staff attendance',
    icon: UsersRound,
    accent: 'teal',
    subModules: [
      { key: 'employees', title: 'Staff directory', description: 'Employees, departments, contracts and documents', path: ROUTES.employees, permissions: [P.EMPLOYEE_VIEW], icon: IdCard },
      { key: 'teachers', title: 'Teachers', description: 'Teaching staff and their specialisations', path: ROUTES.teachers, permissions: [P.TEACHER_VIEW], icon: Presentation },
      { key: 'staff-attendance', title: 'Staff attendance', description: 'Daily staff attendance and monthly summaries', path: ROUTES.staffAttendance, permissions: [P.EMPLOYEE_ATTENDANCE_VIEW], icon: CalendarCheck },
    ],
  },
  {
    key: 'hr-payroll',
    title: 'HR & Payroll',
    description: 'Leave, salaries, payroll runs, payslips and staff loans',
    icon: Briefcase,
    accent: 'amber',
    subModules: [
      { key: 'overview', title: 'HR overview', description: 'Headcount, leave today and payroll status', path: ROUTES.hrDashboard, permissions: [P.EMPLOYEE_VIEW], icon: ChartColumn },
      { key: 'leave', title: 'Leave requests', description: 'Approve, reject and track leave', path: ROUTES.leaveRequests, permissions: [P.LEAVE_REQUEST_VIEW], icon: CalendarDays },
      { key: 'leave-balances', title: 'Leave balances', description: 'Entitlement and days taken per employee', path: ROUTES.leaveBalances, permissions: [P.LEAVE_BALANCE_VIEW], icon: Clock },
      { key: 'leave-types', title: 'Leave types', description: 'Kinds of leave and their rules', path: ROUTES.leaveTypes, permissions: [P.LEAVE_TYPE_MANAGE], icon: Tags },
      { key: 'payroll', title: 'Payroll', description: 'Monthly payroll runs, approval and payslips', path: ROUTES.payroll, permissions: [P.PAYROLL_PERIOD_VIEW], icon: Banknote },
      { key: 'salary-assignments', title: 'Staff salaries', description: 'Salary packages assigned to employees', path: ROUTES.salaryAssignments, permissions: [P.SALARY_ASSIGNMENT_VIEW], icon: Wallet },
      { key: 'salary-structures', title: 'Salary packages', description: 'Reusable pay structures', path: ROUTES.salaryStructures, permissions: [P.SALARY_STRUCTURE_VIEW], icon: Layers },
      { key: 'salary-components', title: 'Salary components', description: 'Allowances and deductions', path: ROUTES.salaryComponents, permissions: [P.SALARY_COMPONENT_MANAGE], icon: Percent },
      { key: 'loans', title: 'Staff loans', description: 'Advances and their repayment through payroll', path: ROUTES.loans, permissions: [P.LOAN_VIEW], icon: HandCoins },
      { key: 'reports', title: 'HR reports', description: 'Payroll and leave reports', path: ROUTES.hrReports, permissions: [P.PAYROLL_PERIOD_VIEW], icon: FileBarChart },
      { key: 'settings', title: 'HR settings', description: 'Working week, leave year and payroll rules', path: ROUTES.hrSettings, permissions: [P.HR_SETTINGS_MANAGE], icon: Settings2 },
    ],
  },
  {
    key: 'workspace',
    title: 'My Workspace',
    description: 'Your notifications, announcements, calendar, leave and payslips',
    icon: LayoutGrid,
    accent: 'cyan',
    subModules: [
      { key: 'my-teaching', title: 'My teaching', description: 'Your week, today\'s lessons and registers to take', path: ROUTES.myTeaching, permissions: [P.TIMETABLE_VIEW], icon: CalendarClock },
      { key: 'notifications', title: 'Notifications', description: 'Everything addressed to you, newest first', path: ROUTES.notifications, permissions: [P.NOTIFICATION_SELF_VIEW], icon: Bell },
      { key: 'announcements', title: 'Announcements', description: 'Notices published to you', path: ROUTES.myAnnouncements, permissions: [P.ANNOUNCEMENT_VIEW], icon: Megaphone },
      { key: 'calendar', title: 'Calendar', description: 'Terms, holidays and school events', path: ROUTES.calendar, permissions: [P.CALENDAR_VIEW], icon: CalendarDays },
      { key: 'my-leave', title: 'My leave', description: 'Request leave and see your balance', path: ROUTES.myLeave, permissions: [P.LEAVE_REQUEST_CREATE], icon: CalendarCheck },
      { key: 'my-payslips', title: 'My payslips', description: 'Your monthly payslips', path: ROUTES.myPayslips, permissions: [P.PAYSLIP_SELF_VIEW], icon: FileText },
      { key: 'my-library', title: 'My library', description: 'Browse the catalogue, your loans and reservations', path: ROUTES.browseCatalogue, permissions: [P.LIBRARY_SELF_VIEW], icon: BookHeart },
      { key: 'my-trips', title: 'My trips', description: 'Your driving duties and trip registers', path: ROUTES.myTransport, permissions: [P.TRANSPORT_SELF_VIEW], icon: Bus },
    ],
  },
  {
    key: 'administration',
    title: 'Administration',
    description: 'School settings, campuses, academic years and data import',
    icon: Building2,
    accent: 'slate',
    subModules: [
      { key: 'schools', title: 'Schools', description: 'School profile and settings', path: ROUTES.schools, permissions: [P.SCHOOL_VIEW], icon: School },
      { key: 'campuses', title: 'Campuses', description: 'Campuses and their details', path: ROUTES.campuses, permissions: [P.CAMPUS_VIEW], icon: Building2 },
      { key: 'academic-years', title: 'Academic years', description: 'Years and terms', path: ROUTES.academicYears, permissions: [P.ACADEMIC_YEAR_VIEW], icon: CalendarRange },
      { key: 'calendar', title: 'School calendar', description: 'Holidays, closures and events', path: ROUTES.calendar, permissions: [P.CALENDAR_VIEW], icon: CalendarDays },
      { key: 'announcement-desk', title: 'Announcement desk', description: 'Write, publish and archive announcements', path: ROUTES.announcements, permissions: [P.ANNOUNCEMENT_MANAGE], icon: Megaphone },
      { key: 'import', title: 'Import data', description: 'Bring students, staff and books in from CSV', path: ROUTES.dataImport, permissions: [P.IMPORT_MANAGE], icon: FileUp },
    ],
  },
  {
    key: 'academic-structure',
    title: 'Academic Structure',
    description: 'Grades, grade offerings, sections and subjects',
    icon: Network,
    accent: 'orange',
    subModules: [
      { key: 'grades', title: 'Grades', description: 'The grade ladder of the school', path: ROUTES.grades, permissions: [P.GRADE_VIEW], icon: GraduationCap },
      { key: 'grade-offerings', title: 'Grade offerings', description: 'Grades offered per campus and year', path: ROUTES.gradeOfferings, permissions: [P.GRADE_OFFERING_VIEW], icon: Network },
      { key: 'sections', title: 'Sections', description: 'Classes within each grade offering', path: ROUTES.sections, permissions: [P.SECTION_VIEW], icon: Users },
      { key: 'subjects', title: 'Subjects', description: 'The subject catalogue', path: ROUTES.subjects, permissions: [P.SUBJECT_VIEW], icon: BookOpen },
    ],
  },
  {
    key: 'library',
    title: 'Library',
    description: 'Catalogue, circulation, members, reservations and fines',
    icon: LibraryBig,
    accent: 'rose',
    subModules: [
      { key: 'overview', title: 'Library overview', description: 'Loans, due today, overdue and reservations', path: ROUTES.libraryDashboard, permissions: [P.LIBRARY_VIEW], icon: ChartColumn },
      { key: 'desk', title: 'Issue & return', description: 'The circulation desk', path: ROUTES.circulationDesk, permissions: [P.CIRCULATION_ISSUE], icon: ArrowLeftRight },
      { key: 'catalogue', title: 'Catalogue', description: 'Books, authors and copies', path: ROUTES.catalogue, permissions: [P.CATALOGUE_VIEW], icon: BookMarked },
      { key: 'members', title: 'Members', description: 'Borrowers and their memberships', path: ROUTES.libraryMembers, permissions: [P.LIBRARY_MEMBER_VIEW], icon: UsersRound },
      { key: 'loans', title: 'Book loans', description: 'Everything on loan', path: ROUTES.loansList, permissions: [P.CIRCULATION_VIEW], icon: BookCopy },
      { key: 'overdue', title: 'Overdue loans', description: 'Loans past their due date', path: ROUTES.overdueLoans, permissions: [P.CIRCULATION_VIEW], icon: AlarmClock },
      { key: 'reservations', title: 'Reservations', description: 'Holds and the queue for each title', path: ROUTES.reservations, permissions: [P.RESERVATION_VIEW], icon: CalendarClock },
      { key: 'fines', title: 'Fines', description: 'Late, lost and damage fines', path: ROUTES.libraryFines, permissions: [P.LIBRARY_FINE_VIEW], icon: Coins },
      { key: 'inventory', title: 'Library stock', description: 'Stock counts of the collection', path: ROUTES.libraryInventory, permissions: [P.LIBRARY_INVENTORY_VIEW], icon: Boxes },
      { key: 'lost-damaged', title: 'Lost & damaged', description: 'Copies reported lost or damaged', path: ROUTES.lostAndDamaged, permissions: [P.LIBRARY_INVENTORY_VIEW], icon: PackageX },
      { key: 'acquisitions', title: 'Acquisitions', description: 'Orders for new books', path: ROUTES.acquisitions, permissions: [P.ACQUISITION_VIEW], icon: ShoppingCart },
      { key: 'suppliers', title: 'Suppliers', description: 'Booksellers and vendors', path: ROUTES.librarySuppliers, permissions: [P.LIBRARY_SUPPLIER_VIEW], icon: Truck },
      { key: 'categories', title: 'Categories', description: 'Subject categories of the collection', path: ROUTES.libraryCategories, permissions: [P.CATALOGUE_VIEW], icon: FolderTree },
      { key: 'reports', title: 'Library reports', description: 'Circulation and collection reports', path: ROUTES.libraryReports, permissions: [P.LIBRARY_REPORT_VIEW], icon: FileBarChart },
      { key: 'settings', title: 'Library settings', description: 'Libraries, locations and borrowing rules', path: ROUTES.librarySettings, permissions: [P.LIBRARY_MANAGE], icon: Settings2 },
    ],
  },
  {
    key: 'transport',
    title: 'Transport',
    description: 'Vehicles, drivers, routes, riders and daily trips',
    icon: Bus,
    accent: 'amber',
    subModules: [
      { key: 'overview', title: 'Transport overview', description: 'Trips today, riders, incidents and alerts', path: ROUTES.transportDashboard, permissions: [P.TRANSPORT_VIEW], icon: ChartColumn },
      { key: 'operations', title: 'Daily operations', description: 'Today\'s trips board', path: ROUTES.transportOperations, permissions: [P.TRANSPORT_TRIP_VIEW], icon: CalendarClock },
      { key: 'trips', title: 'Trips', description: 'Trip history and registers', path: ROUTES.trips, permissions: [P.TRANSPORT_TRIP_VIEW], icon: Navigation },
      { key: 'routes', title: 'Routes', description: 'Routes, stops and schedules', path: ROUTES.transportRoutes, permissions: [P.TRANSPORT_ROUTE_VIEW], icon: RouteIcon },
      { key: 'riders', title: 'Riders', description: 'Students enrolled on transport', path: ROUTES.transportEnrollments, permissions: [P.TRANSPORT_ENROLLMENT_VIEW], icon: UsersRound },
      { key: 'vehicles', title: 'Vehicles', description: 'Fleet, documents and capacity', path: ROUTES.vehicles, permissions: [P.VEHICLE_VIEW], icon: BusFront },
      { key: 'drivers', title: 'Drivers', description: 'Drivers, attendants and licences', path: ROUTES.transportDrivers, permissions: [P.TRANSPORT_DRIVER_VIEW], icon: IdCard },
      { key: 'incidents', title: 'Incidents', description: 'Reported incidents and their follow-up', path: ROUTES.transportIncidents, permissions: [P.TRANSPORT_INCIDENT_VIEW], icon: AlertTriangle },
      { key: 'maintenance', title: 'Maintenance', description: 'Service schedules and repairs', path: ROUTES.transportMaintenance, permissions: [P.VEHICLE_MAINTENANCE_VIEW], icon: Wrench },
      { key: 'reports', title: 'Transport reports', description: 'Ridership, trips and fees', path: ROUTES.transportReports, permissions: [P.TRANSPORT_REPORT_VIEW], icon: FileBarChart },
      { key: 'settings', title: 'Transport settings', description: 'Capacity and document warnings', path: ROUTES.transportSettings, permissions: [P.TRANSPORT_MANAGE], icon: Settings2 },
    ],
  },
  {
    key: 'assets',
    title: 'Assets & Inventory',
    description: 'Asset register, custody, counts and consumable stock',
    icon: Package,
    accent: 'teal',
    subModules: [
      { key: 'register', title: 'Asset register', description: 'Every tagged asset, its custodian and history', path: ROUTES.assets, permissions: [P.ASSET_VIEW], icon: Package },
      { key: 'audits', title: 'Asset counts', description: 'Physical counts and their variances', path: ROUTES.assetAudits, permissions: [P.ASSET_VIEW], icon: PackageSearch },
      { key: 'stock', title: 'Consumable stock', description: 'Stationery and supplies, received and issued', path: ROUTES.stock, permissions: [P.STOCK_VIEW], icon: Warehouse },
      { key: 'setup', title: 'Asset setup', description: 'Categories and locations', path: ROUTES.assetSetup, permissions: [P.ASSET_MANAGE], icon: SlidersHorizontal },
    ],
  },
  {
    key: 'access-control',
    title: 'Access Control',
    description: 'Users, roles, permissions and the audit trail',
    icon: Shield,
    accent: 'slate',
    subModules: [
      { key: 'users', title: 'Users', description: 'Accounts, status and password resets', path: ROUTES.users, permissions: [P.USER_VIEW], icon: UserCog },
      { key: 'roles', title: 'Roles', description: 'Roles and the permissions they grant', path: ROUTES.roles, permissions: [P.ROLE_VIEW], icon: ShieldCheck },
      { key: 'permissions', title: 'Permissions', description: 'The permission catalogue', path: ROUTES.permissions, permissions: [P.PERMISSION_VIEW], icon: KeyRound },
      { key: 'audit', title: 'Audit trail', description: 'Who changed what, and when', path: ROUTES.auditTrail, permissions: [P.AUDIT_VIEW], icon: History },
    ],
  },
];

/** Pages that belong to no module: reached from the sidebar's global items. */
export const GLOBAL_PAGES: { path: string; title: string; permissions: string[]; icon: Icon }[] = [
  { path: ROUTES.insights, title: 'School overview', permissions: [P.DASHBOARD_VIEW], icon: ListChecks },
];

type CanAny = (permissions: string[]) => boolean;

export interface VisibleModule extends ModuleDef {
  /** Only the sub-modules this user may open. */
  subModules: SubModule[];
}

/** The modules this user can open, each trimmed to the sub-modules they can open. */
export function visibleModules(canAny: CanAny): VisibleModule[] {
  return MODULES.map((module) => ({
    ...module,
    subModules: module.subModules.filter((sub) => canAny(sub.permissions)),
  })).filter((module) => module.subModules.length > 0);
}

export function findModule(key: string | undefined): ModuleDef | undefined {
  return MODULES.find((module) => module.key === key);
}

export interface ResolvedLocation {
  module: ModuleDef;
  /** Absent on the module dashboard itself. */
  sub?: SubModule;
  /** True below a sub-module's page: a record opened from its list. */
  isDetail: boolean;
}

/**
 * Where a path sits in the map. Longest matching sub-module path wins, so
 * `/library/loans/overdue` is "Overdue loans", not "Book loans"; a page listed
 * under two modules resolves to the one declared first.
 */
export function resolveLocation(pathname: string): ResolvedLocation | null {
  const dashboardMatch = /^\/dashboard\/([^/]+)\/?$/.exec(pathname);
  if (dashboardMatch !== null) {
    const module = findModule(dashboardMatch[1]);
    return module === undefined ? null : { module, isDetail: false };
  }

  let best: { module: ModuleDef; sub: SubModule } | null = null;
  for (const module of MODULES) {
    for (const sub of module.subModules) {
      const matches = pathname === sub.path || pathname.startsWith(`${sub.path}/`);
      if (matches && (best === null || sub.path.length > best.sub.path.length)) {
        best = { module, sub };
      }
    }
  }
  return best === null ? null : { ...best, isDetail: pathname !== best.sub.path };
}

export interface NavItem {
  label: string;
  path: string;
  /** Shown when the user holds any one of these. Empty means always shown. */
  permissions: string[];
  icon: Icon;
}

export interface NavSection {
  title: string;
  items: NavItem[];
}

/**
 * Every page, grouped by module — what the search palette offers. Derived,
 * never written by hand: a page added to a module is searchable automatically.
 * A page listed under two modules appears once, under the first.
 */
export const NAVIGATION: NavSection[] = (() => {
  const overview: NavSection = {
    title: 'Overview',
    items: [
      { label: 'Dashboard', path: ROUTES.dashboard, permissions: [], icon: LayoutGrid },
      ...GLOBAL_PAGES.map((page) => ({ label: page.title, path: page.path, permissions: page.permissions, icon: page.icon })),
    ],
  };
  const seen = new Set<string>(overview.items.map((item) => item.path));
  const sections: NavSection[] = [overview];
  for (const module of MODULES) {
    const items = module.subModules
      .filter((sub) => !seen.has(sub.path))
      .map((sub) => ({ label: sub.title, path: sub.path, permissions: sub.permissions, icon: sub.icon }));
    items.forEach((item) => seen.add(item.path));
    if (items.length > 0) {
      sections.push({ title: module.title, items });
    }
  }
  return sections;
})();
